import type { OAuthProvider as OAuthProviderId } from '@/lib/api/auth';

/** Query param carrying a login to auto-resume after the loopback host bounce. */
export const RESUME_LOGIN_PARAM = 'resumeOAuthLogin';

const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1']);
const PROVIDERS: ReadonlySet<string> = new Set([
    'google',
    'discord',
    'x',
    'telegram',
]);

export function isOAuthProviderId(value: string): value is OAuthProviderId {
    return PROVIDERS.has(value);
}

/**
 * The loopback host each provider's OAuth client registry actually accepts
 * (verified live): Telegram resolves 127.0.0.1 but rejects the literal
 * "localhost" host, while Google/Discord/X are registered the other way
 * around. MoonHub.API applies the same mapping when it builds the
 * redirect_uri, so the page origin and the redirect target always agree.
 */
export function canonicalLoopbackHostFor(provider: OAuthProviderId): string {
    return provider === 'telegram' ? '127.0.0.1' : 'localhost';
}

/**
 * When developing on the WRONG loopback host for a provider, the whole login
 * ceremony must move to the canonical host BEFORE `/v2/auth/login` runs: that
 * call mints the oauth_pending cookie, and the callback must present it from
 * the same origin — switching hosts mid-flow loses the cookie and the
 * callback fails with "Missing oauth_pending cookie". Returns the URL to
 * bounce the page to (carrying the resume param so the login restarts there
 * automatically), or null when no bounce is needed.
 */
export function loopbackLoginBounceUrl(
    provider: OAuthProviderId,
    location: Pick<Location, 'hostname' | 'href'> = window.location,
): string | null {
    if (!LOOPBACK_HOSTS.has(location.hostname)) return null;
    const canonical = canonicalLoopbackHostFor(provider);
    if (location.hostname === canonical) return null;
    const url = new URL(location.href);
    url.hostname = canonical;
    url.searchParams.set(RESUME_LOGIN_PARAM, provider);
    return url.toString();
}

/**
 * Read-and-strip the resume param from the current URL. Stripping happens
 * unconditionally (and before the login restarts) so a failing login can
 * never loop the bounce.
 */
export function consumeResumeLogin(): OAuthProviderId | null {
    const url = new URL(window.location.href);
    const value = url.searchParams.get(RESUME_LOGIN_PARAM);
    if (value === null) return null;
    url.searchParams.delete(RESUME_LOGIN_PARAM);
    window.history.replaceState(null, '', url.toString());
    return isOAuthProviderId(value) ? value : null;
}
